import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BETTER_AUTH_BASE_PATH } from '@/libs/better-auth-client';
import { clearRedirectCookie, toAbsoluteAppUrl } from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';

/**
 * Email-verification landing page. The verification email links here (frontend origin) so the user
 * lands on the web app. Verification is a top-level `GET /auth/verify-email` navigation that consumes
 * the token, marks the email verified, sets the session cookie, and 302-redirects to `callbackURL`.
 * So this page forwards the token to that endpoint with an absolute frontend `callbackURL`. Must be a
 * PUBLIC route: the visitor may be unauthenticated, and a private route would trip the session query
 * → Apollo bounces to `/auth/login?redirectTo=…`.
 */
export default function AuthVerifyEmail() {
    const { t } = useTranslation();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (!token) {
            window.location.replace('/auth/login');
            return;
        }

        // Land on the app after verification. Only honour the link's own `callbackURL` if it's a safe
        // same-origin app path — never an `/auth/*` page (would loop the verify), and NOT
        // getRedirectPath()/the REDIRECT_AFTER_LOGIN cookie (a stale auth URL may be parked there).
        clearRedirectCookie();
        const rawCallback = params.get('callbackURL') || '';
        const isSafeAppPath = /^\/(?!\/)/.test(rawCallback) && !rawCallback.startsWith('/auth/');
        const callbackURL = toAbsoluteAppUrl(isSafeAppPath ? rawCallback : '/home');
        const errorCallbackURL = toAbsoluteAppUrl('/auth/login');

        const verifyUrl =
            `${import.meta.env.VITE_API_ROOT}${BETTER_AUTH_BASE_PATH}/verify-email` +
            `?token=${encodeURIComponent(token)}` +
            `&callbackURL=${encodeURIComponent(callbackURL)}` +
            `&errorCallbackURL=${encodeURIComponent(errorCallbackURL)}`;

        window.location.replace(verifyUrl);
    }, []);

    return (
        <AuthSplitLayout>
            <Stack alignItems="center" justifyContent="center" minHeight={200}>
                <Typography>{t('auth.common.loading')}</Typography>
            </Stack>
        </AuthSplitLayout>
    );
}
